import { plural } from "./textUtils";
import { isDefined } from "./date";
import { EmptyString } from "constants/strings";
import { CONTRACT_CURRENCY_ENUM, NetworkType } from "interfaces";
import { WALLET_NETWORK } from "./walletUtils";

export enum CURRENCY_TYPE {
  RUB = "RUB",
  EUR = "EUR",
  USD = "USD",
}

export enum CURRENCY {
  RUB = "₽",
  EUR = "€",
  USD = "$",
}
export enum RUB_CURRENCY {
  RUB = "руб.",
}

const zeroDecimalsFormatter = new Intl.NumberFormat("ru-RU", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export function formatZeroDecimals(value: number | string) {
  return zeroDecimalsFormatter.format(Number(value)).replace(/\s/g, "\u2009");
}

const twoDecimalsFormatter = new Intl.NumberFormat("ru-RU", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function formatWithTwoDecimals(value: number | string) {
  return twoDecimalsFormatter.format(Number(value));
}

export function formatWithTwoDecimalsRub(value: number) {
  return `${formatWithTwoDecimals(value)} ₽`;
}

export const showRecords = (num: number) => num + " " + plural(num, ["запись", "записи", "записей"]);

export const showMonths = (num: number) => num + " " + plural(num, ["месяц", "месяца", "месяцев"]);

export const showPeople = (num: number) => num + " " + plural(num, ["человек", "человека", "человек"]);

function splitMajorPart(amount: string, partSize = 3, splitter = String.fromCharCode(8201), splitFrom = 4) {
  if (amount.length < splitFrom) {
    return amount;
  }
  return amount.replace(new RegExp("\\B(?=(\\d{" + partSize + "})+(?!\\d))", "g"), splitter);
}

export function formatAmount(
  value: number,
  { currency = CURRENCY_TYPE.RUB, minority = 100, decimalsOptional = true, separator = ",", unit = "" }
) {
  if (!isDefined(value)) {
    return EmptyString;
  }
  minority = minority === 0 ? 1 : minority;
  let fractionDigits = Math.log(minority) * Math.LOG10E;
  let valueAbsStr = (Math.abs(value) / minority).toFixed(fractionDigits);
  let parts = valueAbsStr.split(".");
  let majorPart = value < 0 ? "−" + splitMajorPart(parts[0]) : splitMajorPart(parts[0]);
  let minorPart = decimalsOptional && value % minority === 0 ? "" : parts[1];
  let formattedAmount = minorPart ? majorPart + separator + minorPart : majorPart;
  return formattedAmount + String.fromCharCode(8201) + CURRENCY[currency] + unit;
}

/**
 * Возвращает стандартный числовой формат с точкой в качестве разделителя десятичной части
 */
export function normalizeDecimalSeparator(value: string | number): string {
  if (value === null || value === undefined) {
    return "";
  }

  const strValue = String(value);

  return strValue.replace(",", ".");
}

const formatterCache = new Map<number, Intl.NumberFormat>();

function getNumberFormatter(decimals: number): Intl.NumberFormat {
  let f = formatterCache.get(decimals);
  if (!f) {
    f = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
      useGrouping: true,
    });
    formatterCache.set(decimals, f);
  }
  return f;
}

export function formatWithDecimals(value: number | string | undefined, decimals: number = 2) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const num = typeof value === "string" ? parseFloat(normalizeDecimalSeparator(value)) : value;

  if (isNaN(num)) {
    throw new Error("Неверное значение");
  }

  const formatter = getNumberFormatter(decimals);
  return formatter.format(num).replace(/,/g, " ");
}

/**
 * Преобразует число или строку с пробелами/запятой в числовое значение.
 */
export const parseNumeric = (value: number | string | undefined) =>
  typeof value === "string" ? Number(value.replace(/\s/g, "").replace(",", ".")) : Number(value);

/**
 * Переводит процентное значение в долю: 12.5 -> 0.125.
 */
export const toFraction = (value: number | string | undefined) => Number((parseNumeric(value) / 100).toFixed(4));

/**
 * Переводит процент в долю, а пустое значение возвращает как 0.
 */
export const toFractionOrZero = (value: number | string | undefined) =>
  value !== undefined && value !== "" ? toFraction(value) : 0;

/**
 * Преобразует значение в число, а пустое значение возвращает как 0.
 */
export const toNumberOrZero = (value: number | string | undefined) =>
  value !== undefined && value !== "" ? parseNumeric(value) : 0;

export const formatBalance = (value: number | string, currency: string) => {
  const isTwoDecimalsCoin = currency === CONTRACT_CURRENCY_ENUM.USDT || currency === CONTRACT_CURRENCY_ENUM.USDC;
  const decimals = isTwoDecimalsCoin ? 2 : 4;
  return formatWithDecimals(value, decimals);
};

export const generateRandomInteger = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

/**
 * Возвращает количество знаков после запятой для разных сетей
 * @param network - тип сети
 * @returns количество знаков после запятой
 */
export function getNetworkDecimalPlaces(network: NetworkType): number {
  switch (network) {
    case WALLET_NETWORK.ETH:
      return 3;
    case WALLET_NETWORK.TRX:
    default:
      return 2;
  }
}

export function trimTrailingZeros(value: number | string): string {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const strValue = String(value);

  if (!strValue.includes(".")) {
    return strValue;
  }

  return strValue.replace(/\.?0+$/, "");
}
